import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container, Typography, Box, Grid, Chip, Card, CardContent, CardMedia, CircularProgress,
  Tabs, Tab, Paper,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel,
  Tooltip,
  IconButton,
  Link as MuiLink,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip } from 'recharts';
import { useSearchParams } from 'react-router-dom';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import axios from 'axios';
import { AxiosError } from 'axios';
import { BandRecommendation } from '../types/BandRecommendations';
import { createYouTubeEmbed } from '../utils/youtubeEmbedUtil';
import EditIcon from '@mui/icons-material/Edit';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import TikTokIcon from '@mui/icons-material/MusicVideo';
import PublishBandModal from '../components/PublishBandModal';
import PublishedStrandsCard from '../components/PublishedStrandsCard';
import AdaptiveImage from '../components/artists/AdaptiveBandImage';
import ImageCarousel from '../components/form/ImageCarousel';
import PressReviewsTab from '../components/bandpage/PressReviewsTab';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import BandQRModal from '../components/bandpage/BandQRModal';
import PromoterInterestModal from '../components/public/PromoterInterestModal';
import HandshakeIcon from '@mui/icons-material/Handshake';
import LanguageIcon from '@mui/icons-material/Language';
import PublicAudioPlayer from '../components/public/PublicAudioPlayer';
import ShareButton from '../components/shared/ShareButton';
import { trackEvent } from '../utils/analytics';

// Context and switcher imports
import { useBandView } from '../contexts/BandViewContext';
import { performerTitleByInstrument } from '../constants/instrumentation';
import AdminViewSwitcher from '../components/bandpage/AdminViewSwitcher';
import ArtistViewSwitcher from '../components/bandpage/ArtistViewSwitcher';

interface Event {
  _id: string;
  name: string;
  date: string;
}

// Create an axios instance with default config
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests if it exists
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`band-tabpanel-${index}`}
      aria-labelledby={`band-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const BandDetailPage: React.FC = () => {
  const { id, bandId } = useParams<{ id?: string; bandId?: string }>();
  const currentBandId = id || bandId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get view configuration from context
  const { viewConfig, viewMode, currentUser }: { viewConfig: any; viewMode: string; currentUser: any } = useBandView();
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [interestModalOpen, setInterestModalOpen] = useState(false);
  const [band, setBand] = useState<BandRecommendation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [openAddToEventDialog, setOpenAddToEventDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openPublishModal, setOpenPublishModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [mediaTabMounted, setMediaTabMounted] = useState(false);
  const [similarSegments, setSimilarSegments] = useState<{ label: string; type: string; bands: (BandRecommendation & { _sharedPerformers?: { name: string; instrument: string }[] })[] }[]>([]);

  const handlePublish = () => {
    setOpenPublishModal(true);
  };

  useEffect(() => {
    const fetchBandDetails = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get<BandRecommendation>(
          `/api/bandrecommendations/${currentBandId}`
        );
        const updatedBand = {
          ...response.data,
          youtubeEmbeds: response.data.youtubeEmbeds.map(embed =>
            embed.startsWith('<iframe') ? embed : createYouTubeEmbed(embed)
          )
        };
        setBand(updatedBand);
        setError(null);
      } catch (error) {
        console.error('Error fetching band details:', error);
        setError('An unexpected error occurred while fetching band details.');
      } finally {
        setLoading(false);
      }
    };

    const fetchEvents = async () => {
      try {
        const response = await axiosInstance.get<Event[]>(`/api/events`);
        setEvents(response.data);
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };

    const fetchSimilar = async () => {
      try {
        const { data } = await axiosInstance.get(`/api/bandrecommendations/similar/${currentBandId}`);
        setSimilarSegments(data);
      } catch { /* similar bands are non-critical */ }
    };

    fetchBandDetails();
    fetchEvents();
    fetchSimilar();
  }, [currentBandId]);

  // Track page view once after band data loads
  useEffect(() => {
    if (!band) return;
    trackEvent('page_view', { bandId: band._id, bandName: band.artistName });
  }, [band?._id]);

  // Sticky header on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 420);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Pre-load media tab 3s after band loads so iframes are ready when clicked
  useEffect(() => {
    if (!band) return;
    const t = setTimeout(() => setMediaTabMounted(true), 3000);
    return () => clearTimeout(t);
  }, [band]);

  const handleAddToEvent = () => {
    setOpenAddToEventDialog(true);
  };

  const handleCreateNewEvent = () => {
    navigate('/events/new', { state: { bandId: currentBandId } });
  };

  const handleAddToExistingEvent = async () => {
    if (selectedEvent && band) {
      try {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/events/${selectedEvent}/performances`, {
          band: band._id,
        });
        setOpenAddToEventDialog(false);
      } catch (error) {
        console.error('Error adding band to event:', error);
      }
    }
  };

const handlePublishBand = async (strandId: string, shortDescription: string) => {
  try {
    const payload = {
      strandIds: [strandId],
      shortDescription,
    };

    // 1. âœ… API CALL - Send to server
    const response = await axiosInstance.post(
      `/api/bandrecommendations/${band?._id}/publish`,
      payload
    );

    // 2. âœ… SIMPLE UPDATE - Just refetch the band data to avoid type issues
    if (response.status === 200 || response.status === 201) {
      console.log('ðŸ”„ Refetching band data...');
      const updatedBandResponse = await axiosInstance.get(
        `/api/bandrecommendations/${band?._id}`
      );
      console.log('ðŸ”„ Updated band data:', updatedBandResponse.data);
      console.log('ðŸ”„ Updated band.publishedTo:', updatedBandResponse.data.publishedTo);
      setBand(updatedBandResponse.data);
    } else {
      console.log('âŒ Unexpected response status:', response.status);
    }

    setOpenPublishModal(false);
    
  } catch (error: unknown) {
    console.error('âŒ Error publishing band:', error);

    if (axios.isAxiosError(error)) {
      if (error.response?.status === 403) {
        alert('You do not have permission to publish bands. Please contact an administrator.');
      } else if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || error.response?.data || 'Bad request';
        alert(`Bad request: ${errorMessage}`);
      } else {
        alert(`An error occurred while publishing the band: ${error.message}`);
      }
    } else {
      alert('An unexpected error occurred while publishing the band.');
    }
  }
};

  const handlePending = () => {
    alert('Pending functionality to be implemented.');
  };

  const handleToggleDeactivate = async () => {
    if (!band?._id) return;
    const action = band.isDeactivated ? 'activate' : 'deactivate';
    try {
      const response = await axiosInstance.patch(`/api/bandrecommendations/${band._id}/${action}`);
      setBand(response.data);
    } catch (error) {
      console.error(`Error ${action}ing band:`, error);
    }
  };

  const handleDelete = () => {
    setOpenDeleteDialog(true);
  };

  const handleEdit = () => {
    navigate(`/bandrecommendations/${currentBandId}/edit`);
  };

  const handleModalClose = async () => {
  setOpenPublishModal(false);
  // Refetch band data whenever modal closes
  if (band?._id) {
    const updatedBandResponse = await axiosInstance.get(
      `/api/bandrecommendations/${band._id}`
    );
    setBand(updatedBandResponse.data);
  }
};

const confirmDelete = async () => {
  if (band) {
    try {
      // âœ… FIX: Use axiosInstance instead of plain axios
      await axiosInstance.delete(`/api/bandrecommendations/${band._id}`);
      setOpenDeleteDialog(false);
      navigate('/bandrecommendations');
    } catch (error) {
      console.error('Error deleting band recommendation:', error);
    }
  }
};

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!band) return <Typography>Band not found.</Typography>;

  // Calculate dynamic layout based on visible content
  const rightColumnItems = [
    viewConfig.showAdminActions,
    true, // PublishedStrandsCard always shows
    viewConfig.showRecommenderInfo,
    viewConfig.showContactInfo,
    viewConfig.showAvailability
  ].filter(Boolean).length;

  // Determine grid sizes based on content
  const leftColSize = rightColumnItems <= 1 ? 12 : rightColumnItems <= 2 ? 8 : 6;
  const rightColSize = rightColumnItems <= 1 ? 0 : rightColumnItems <= 2 ? 4 : 6;

  // Dynamic image height based on available space
  const dynamicImageHeight = rightColumnItems <= 1 ? 600 : rightColumnItems <= 2 ? 500 : 400;

  const genderData = [
    { name: 'Male', value: band.maleMembers, color: '#2196f3' },
    { name: 'Female', value: band.femaleMembers, color: '#f50057' },
  ];

  const CustomizedLegend = () => (
    <Box display="flex" justifyContent="center" alignItems="center">
      <Box display="flex" alignItems="center" mr={2}>
        <MaleIcon style={{ color: '#2196f3' }} />
        <Typography variant="body2" ml={1}>Male</Typography>
      </Box>
      <Box display="flex" alignItems="center">
        <FemaleIcon style={{ color: '#f50057' }} />
        <Typography variant="body2" ml={1}>Female</Typography>
      </Box>
    </Box>
  );

  const formatDate = (date: Date) => {
    return {
      month: date.toLocaleString('default', { month: 'short' }),
      day: date.getDate(),
      year: date.getFullYear(),
    };
  };

  const availabilityFrom = formatDate(new Date(band.availability.from));
  const availabilityTo = formatDate(new Date(band.availability.to));

// Determine which tabs to show based on viewConfig
const tabs: { label: string; index: number }[] = [];
let tabIndex = 0;

// Overview tab - always shown
tabs.push({ label: 'Overview', index: tabIndex++ });

// Media tab - shown if media is enabled
if (viewConfig.showFullMedia || viewConfig.showSocialMedia) {
  tabs.push({ label: 'Media & Social', index: tabIndex++ });
}

// Press & Reviews - only if band has validation entries
if (band.externalValidation && band.externalValidation.length > 0) {
  tabs.push({ label: 'Press & Reviews', index: tabIndex++ });
}

// Technical & Financial - shown for admin/artist/strand views
if (viewConfig.showTechnicalRequirements || viewConfig.showFinancialDetails) {
  tabs.push({ label: 'Technical & Financial', index: tabIndex++ });
}

// Admin tab - only for admin view
if (viewConfig.showAdminActions) {
  tabs.push({ label: 'Admin', index: tabIndex++ });
}


return (
  <>
  {/* Sticky mini-header — slides in after scrolling past hero */}
  <Paper
    elevation={4}
    sx={{
      position: 'fixed',
      top: 0, left: 0, right: 0,
      zIndex: 1300,
      borderRadius: 0,
      background: 'rgba(15,15,20,0.96)',
      backdropFilter: 'blur(10px)',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      py: 1.25, px: { xs: 2, md: 4 },
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      transform: scrolled ? 'translateY(0)' : 'translateY(-100%)',
      transition: 'transform 0.3s ease',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: 'white' } }}>
        <ArrowBackIcon fontSize="small" />
      </IconButton>
      <Typography variant="h6" fontWeight={700} color="white" noWrap>{band.artistName}</Typography>
    </Box>
    <Box sx={{ display: 'flex', gap: 1 }}>
      {viewMode === 'public' && (
        <Button size="small" variant="contained" startIcon={<HandshakeIcon sx={{ fontSize: 16 }} />}
          onClick={() => setInterestModalOpen(true)}
          sx={{ bgcolor: '#50E3C2', color: '#0a0a0f', fontWeight: 700, '&:hover': { bgcolor: '#7EECD4' } }}>
          Express Interest
        </Button>
      )}
      {viewMode === 'strand' && (
        <Button size="small" variant="contained" color="success" startIcon={<EmailIcon sx={{ fontSize: 16 }} />}
          onClick={() => { window.location.href = `mailto:ken@improvisedmusic.ie?subject=${encodeURIComponent(`Booking Inquiry for ${band.artistName}`)}`; }}>
          Book This Band
        </Button>
      )}
    </Box>
  </Paper>

  <Container maxWidth="lg">
    <Box my={4}>
      {/* Admin View Switcher */}
      {!searchParams.get('view') && viewConfig.showAdminActions && <AdminViewSwitcher />}

      {/* Artist View Switcher — only for authenticated artists, not public visitors */}
      {(viewMode === 'artist' || viewMode === 'public') && !viewConfig.showAdminActions && currentUser?.role === 'artist' && <ArtistViewSwitcher />}

      {/* ── Hero: image carousel with overlaid name, chips & actions ── */}
      <Box sx={{ position: 'relative', borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        <ImageCarousel
          images={[band.mainImage || '/placeholder-image.jpg', ...(band.additionalImages || [])]}
          positions={[band.mainImagePosition || '50% 50%', ...(band.additionalImagePositions || [])]}
          artistName={band.artistName}
          height={520}
        />
        {/* Gradient overlay */}
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.15) 100%)',
        }} />
        {/* Content overlay */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 2.5, md: 4 }, zIndex: 2 }}>
          {/* Back button */}
          <Button
            startIcon={<ArrowBackIcon />}
            size="small"
            onClick={() => navigate(-1)}
            sx={{ color: 'rgba(255,255,255,0.65)', mb: 1.5, '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            All Artists
          </Button>

          <Typography variant="h1" sx={{
            color: 'white', fontWeight: 700,
            fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem' },
            textShadow: '0 2px 12px rgba(0,0,0,0.6)', mb: 1.5, lineHeight: 1.1,
          }}>
            {band.artistName}
          </Typography>

          {/* Chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
            <Chip label={`${band.maleMembers + band.femaleMembers} Members`} size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', backdropFilter: 'blur(4px)' }} />
            {band.genres.slice(0, 4).map(genre => (
              <Chip key={genre} label={genre} size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', backdropFilter: 'blur(4px)' }} />
            ))}
            {band.activityStatus && band.activityStatus !== 'active' && (
              <Chip label={band.activityStatus.charAt(0).toUpperCase() + band.activityStatus.slice(1)} size="small"
                sx={{ bgcolor: 'rgba(80,227,194,0.2)', color: '#50E3C2', border: '1px solid rgba(80,227,194,0.4)' }} />
            )}
            {band.availability && (
              <Chip label="Available" size="small"
                sx={{ bgcolor: 'rgba(76,175,80,0.25)', color: '#81c784', border: '1px solid rgba(76,175,80,0.4)' }} />
            )}
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {!searchParams.get('view') && viewConfig.showAdminActions && (
              <Tooltip title="Share with QR Code">
                <IconButton onClick={() => setQrModalOpen(true)}
                  sx={{ bgcolor: 'primary.main', color: 'white', width: 48, height: 48, '&:hover': { bgcolor: 'primary.dark', transform: 'scale(1.05)' } }}>
                  <QrCode2Icon />
                </IconButton>
              </Tooltip>
            )}
            <ShareButton
              url={window.location.href}
              title={band.artistName}
              description={band.genres.slice(0, 2).join(' · ')}
              bandId={band._id}
              bandName={band.artistName}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', width: 48, height: 48, backdropFilter: 'blur(4px)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', transform: 'scale(1.05)' } }}
            />
            {viewMode === 'public' && (
              <Button variant="contained" startIcon={<HandshakeIcon />} onClick={() => setInterestModalOpen(true)}
                sx={{ bgcolor: 'rgba(80,227,194,0.9)', color: '#0a0a0f', fontWeight: 700, '&:hover': { bgcolor: '#50E3C2' } }}>
                Express Interest
              </Button>
            )}
            {viewMode === 'strand' && (
              <Button variant="contained" color="success" startIcon={<EmailIcon />}
                onClick={() => {
                  const subject = encodeURIComponent(`Booking Inquiry for ${band.artistName}`);
                  const body = encodeURIComponent(`Hi Kenneth, I'm interested in booking ${band.artistName}.\n\nBand Details: ${window.location.href}`);
                  window.location.href = `mailto:ken@improvisedmusic.ie?subject=${subject}&body=${body}`;
                }}>
                Book This Band
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* PromoterInterestModal */}
      {viewMode === 'public' && (
        <PromoterInterestModal open={interestModalOpen} bandName={band.artistName} bandId={band._id} onClose={() => setInterestModalOpen(false)} />
      )}

      {/* Featured validation — above tabs, type-aware */}
      {(() => {
        const pressQuote = band.externalValidation?.find(v => v.type === 'press_review' && v.featured && v.quote)
                        ?? band.externalValidation?.find(v => v.type === 'press_review' && v.quote);
        const featuredAward = band.externalValidation?.find(v => v.type === 'award' && v.featured)
                           ?? band.externalValidation?.find(v => v.type === 'award');
        if (!pressQuote && !featuredAward) return null;

        return (
          <Box sx={{ my: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Press quote — italic pull-quote with share */}
            {pressQuote && (() => {
              const clean = pressQuote.quote!.replace(/^["""'']+|["""'']+$/g, '').trim();
              return (
                <Box sx={{
                  position: 'relative',
                  px: { xs: 2, md: 4 }, py: 2.5,
                  borderLeft: '4px solid', borderColor: 'primary.main',
                  bgcolor: 'background.paper', borderRadius: '0 8px 8px 0',
                }}>
                  <FormatQuoteIcon sx={{ color: 'primary.main', opacity: 0.4, fontSize: 26, mb: 0.5, display: 'block' }} />
                  <Typography variant="h6" fontStyle="italic" sx={{ lineHeight: 1.55, color: 'text.primary', mb: 1, pr: 5 }}>
                    "{clean}"
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    — {pressQuote.source}{pressQuote.author ? `, ${pressQuote.author}` : ''}
                  </Typography>
                  <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <ShareButton
                      url={window.location.href}
                      title={band.artistName}
                      description={`"${clean}" — ${pressQuote.source}${pressQuote.author ? `, ${pressQuote.author}` : ''}`}
                      sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                    />
                  </Box>
                </Box>
              );
            })()}

            {/* Award — trophy callout, never shown as a quote */}
            {featuredAward && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2,
                px: { xs: 2, md: 4 }, py: 2,
                borderLeft: '4px solid', borderColor: 'warning.main',
                bgcolor: 'rgba(255, 193, 7, 0.07)', borderRadius: '0 8px 8px 0',
              }}>
                <EmojiEventsIcon sx={{ color: 'warning.main', fontSize: 36, flexShrink: 0 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                    {featuredAward.quote ? featuredAward.quote.replace(/^["""'']+|["""'']+$/g, '').trim() : featuredAward.source}
                  </Typography>
                  {featuredAward.quote && (
                    <Typography variant="body2" color="text.secondary">
                      {featuredAward.source}{featuredAward.date ? ` · ${new Date(featuredAward.date).getFullYear()}` : ''}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        );
      })()}

      {/* Audio player — above tabs, only if band has YouTube */}
      {band.youtubeEmbeds && band.youtubeEmbeds.length > 0 && (
        <PublicAudioPlayer bands={[band]} />
      )}

      {/* Tabs Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => {
            setActiveTab(newValue);
            // Mount media tab immediately on click if not already mounted
            const mediaIdx = tabs.findIndex(t => t.label === 'Media & Social');
            if (newValue === mediaIdx) setMediaTabMounted(true);
          }}
          aria-label="band information tabs"
        >
          {tabs.map((tab) => (
            <Tab key={tab.index} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {/* Tab Panels */}
      
{/* OVERVIEW TAB */}
<TabPanel value={activeTab} index={0}>
  <Grid container spacing={3}>
    <Grid item xs={12} md={8}>

      {/* About Section */}
      {band.about && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h5">About</Typography>
              {viewConfig.showAdminActions && (
                <Tooltip title="Edit About">
                  <IconButton size="small" component={Link} to={`/bandrecommendations/${band._id}/edit?step=Band%2FArtist%20Information`} sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box 
              sx={{
                '& h1': { fontSize: '2rem', fontWeight: 700, mt: 2, mb: 1 },
                '& h2': { fontSize: '1.5rem', fontWeight: 700, mt: 2, mb: 1 },
                '& h3': { fontSize: '1.25rem', fontWeight: 700, mt: 2, mb: 1 },
                '& p': { mb: 1 },
                '& img': { maxWidth: '100%', borderRadius: 1, my: 1 },
                '& iframe': { width: '100%', borderRadius: 1, my: 2 },
                '& blockquote': {
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  paddingLeft: 2,
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  my: 2
                },
                '& ul, & ol': { pl: 3, mb: 1 },
                '& a': { color: 'primary.main', textDecoration: 'underline' },
                '& hr': {
                  border: 'none',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  my: 2
                }
              }}
              dangerouslySetInnerHTML={{ __html: band.about }}
            />
          </CardContent>
        </Card>
      )}

{/* Instrumentation */}
{band.instrumentation && band.instrumentation.length > 0 && (
  <Card sx={{ mb: 3 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5">Instrumentation</Typography>
        {viewConfig.showAdminActions && (
          <Tooltip title="Edit Instrumentation">
            <IconButton size="small" component={Link} to={`/bandrecommendations/${band._id}/edit?step=Band%2FArtist%20Information`} sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0 }}>
        {band.instrumentation.map((instrument, index) => (
          <Box key={index} sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            py: 1, px: 1.5,
            borderBottom: '1px solid', borderColor: 'divider',
            bgcolor: index % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mr: 2 }}>
              {instrument.instrumentName}
            </Typography>
            {instrument.performer && (
              <Typography
                variant="body2"
                component={Link}
                to={`/musician/${encodeURIComponent(instrument.performer.name)}`}
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, whiteSpace: 'nowrap' }}
              >
                {instrument.performer.name}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </CardContent>
  </Card>
)}

          </Grid>

<Grid item xs={12} md={4}>
            {/* Gender Balance Bar */}
            {viewConfig.showDemographics && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h5">Band Makeup</Typography>
                    {viewConfig.showAdminActions && (
                      <Tooltip title="Edit Band Makeup">
                        <IconButton size="small" component={Link} to={`/bandrecommendations/${band._id}/edit?step=Band%2FArtist%20Information`} sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="body2" gutterBottom>
                    {band.maleMembers} Male · {band.femaleMembers} Female
                  </Typography>
                  <Box sx={{ display: 'flex', height: 40, borderRadius: 1, overflow: 'hidden', mt: 1 }}>
                    {band.maleMembers > 0 && (
                      <Box 
                        sx={{ 
                          bgcolor: '#2196f3', 
                          width: `${(band.maleMembers / (band.maleMembers + band.femaleMembers)) * 100}%`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}
                      >
                        <MaleIcon sx={{ mr: 0.5, fontSize: '1rem' }} /> {Math.round((band.maleMembers / (band.maleMembers + band.femaleMembers)) * 100)}%
                      </Box>
                    )}
                    {band.femaleMembers > 0 && (
                      <Box 
                        sx={{ 
                          bgcolor: '#f50057', 
                          width: `${(band.femaleMembers / (band.maleMembers + band.femaleMembers)) * 100}%`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}
                      >
                        <FemaleIcon sx={{ mr: 0.5, fontSize: '1rem' }} /> {Math.round((band.femaleMembers / (band.maleMembers + band.femaleMembers)) * 100)}%
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Secondary validation in sidebar — type-aware, never mixes quote and award treatment */}
            {(() => {
              const primaryPress = band.externalValidation?.find(v => v.type === 'press_review' && v.featured && v.quote);
              const secondaryPress = band.externalValidation?.find(v => v.type === 'press_review' && v.quote && v !== primaryPress);
              const pressQ = secondaryPress ?? primaryPress;

              const sidebarAward = !pressQ
                ? (band.externalValidation?.find(v => v.type === 'award' && v.featured)
                  ?? band.externalValidation?.find(v => v.type === 'award'))
                : null;

              if (pressQ) {
                const clean = pressQ.quote!.replace(/^["""'']+|["""'']+$/g, '').trim();
                return (
                  <Card sx={{ mb: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                    <CardContent>
                      <FormatQuoteIcon sx={{ color: 'primary.main', opacity: 0.35, fontSize: 22, mb: 0.5, display: 'block' }} />
                      <Typography variant="body1" fontStyle="italic" sx={{ lineHeight: 1.55, mb: 1 }}>
                        "{clean}"
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        — {pressQ.source}{pressQ.author ? `, ${pressQ.author}` : ''}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              }

              if (sidebarAward) {
                return (
                  <Card sx={{ mb: 2, borderLeft: '3px solid', borderColor: 'warning.main', bgcolor: 'rgba(255,193,7,0.05)' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <EmojiEventsIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                        <Typography variant="overline" color="warning.main" sx={{ lineHeight: 1 }}>Award</Typography>
                      </Box>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        {sidebarAward.quote ? sidebarAward.quote.replace(/^["""'']+|["""'']+$/g, '').trim() : sidebarAward.source}
                      </Typography>
                      {sidebarAward.quote && (
                        <Typography variant="caption" color="text.secondary">
                          {sidebarAward.source}{sidebarAward.date ? ` · ${new Date(sidebarAward.date).getFullYear()}` : ''}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                );
              }
              return (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6">Genres</Typography>
                      {viewConfig.showAdminActions && (
                        <Tooltip title="Edit Genres">
                          <IconButton size="small" component={Link} to={`/bandrecommendations/${band._id}/edit?step=Categorisation`} sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {band.genres.map(genre => <Chip key={genre} label={genre} size="small" />)}
                    </Box>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Sounds Like */}
            {band.similarArtists && band.similarArtists.length > 0 && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">Sounds Like</Typography>
                    {viewConfig.showAdminActions && (
                      <Tooltip title="Edit Sounds Like">
                        <IconButton size="small" component={Link} to={`/bandrecommendations/${band._id}/edit?step=Categorisation`} sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {band.similarArtists.map(artist => <Chip key={artist} label={artist} size="small" variant="outlined" />)}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Availability - Show if enabled (strand/judge/artist/admin) */}
            {viewConfig.showAvailability && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">Availability</Typography>
                    {viewConfig.showAdminActions && (
                      <Tooltip title="Edit Availability">
                        <IconButton size="small" component={Link} to={`/bandrecommendations/${band._id}/edit?step=Availability`} sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 0 } }}>
                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                      <Typography variant="h4" sx={{ fontSize: { xs: '1.4rem', md: '2.125rem' } }}>{availabilityFrom.month}</Typography>
                      <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{availabilityFrom.day}</Typography>
                      <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>{availabilityFrom.year}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', px: { sm: 1 } }}>
                      <ArrowForwardIcon fontSize="large" sx={{ transform: { xs: 'rotate(90deg)', sm: 'none' } }} />
                    </Box>
                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                      <Typography variant="h4" sx={{ fontSize: { xs: '1.4rem', md: '2.125rem' } }}>{availabilityTo.month}</Typography>
                      <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{availabilityTo.day}</Typography>
                      <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>{availabilityTo.year}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Booking Details - Show if enabled (strand/judge/artist/admin) */}
            {viewConfig.showRecommenderInfo && band.recommenderName && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">Page Owner</Typography>
                    {viewConfig.showAdminActions && (
                      <Tooltip title="Edit Page Owner">
                        <IconButton size="small" component={Link} to={`/bandrecommendations/${band._id}/edit?step=Page%20Owner%20Info`} sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="body2"><strong>Name:</strong> {band.recommenderName}</Typography>
                  <Typography variant="body2"><strong>Email:</strong> {band.recommenderEmail}</Typography>
                </CardContent>
              </Card>
            )}

            {/* Contact Info - Show if enabled */}
            {viewConfig.showContactInfo && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h5">Band Contact</Typography>
                    {viewConfig.showAdminActions && (
                      <Tooltip title="Edit Band Contact">
                        <IconButton size="small" component={Link} to={`/bandrecommendations/${band._id}/edit?step=Contact%20Information`} sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="body2"><strong>Name:</strong> {band.contactName}</Typography>
                  <Typography variant="body2"><strong>Email:</strong> {band.contactEmail}</Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      {/* MEDIA & SOCIAL TAB — lazy mounted (pre-loads 3s after page load, or on first click) */}
      {(viewConfig.showFullMedia || viewConfig.showSocialMedia) && (() => {
        const mediaIdx = tabs.findIndex(t => t.label === 'Media & Social');
        return (
        <Box
          role="tabpanel"
          hidden={activeTab !== mediaIdx}
          id={`band-tabpanel-${mediaIdx}`}
          aria-labelledby={`band-tab-${mediaIdx}`}
        >
          {mediaTabMounted && <Box sx={{ py: 3 }}>

          {/* ── Artist Links box — always at top ── */}
          {(band.performanceLink || (viewConfig.showSocialMedia && (band.instagramHandle || band.twitterHandle || band.facebookPage || band.tikTokHandle))) && (
            <Card sx={{ mb: 3, border: '1px solid rgba(255,255,255,0.08)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2, fontSize: '0.7rem' }}>
                    {band.artistName} Links
                  </Typography>
                  {viewConfig.showAdminActions && (
                    <Tooltip title="Edit Media Links">
                      <IconButton size="small" component={Link} to={`/bandrecommendations/${band._id}/edit?step=Media%20Links`} sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
                  {/* Website link */}
                  {band.performanceLink && (
                    <Tooltip title={band.performanceLink}>
                      <IconButton
                        component="a"
                        href={band.performanceLink}
                        target="_blank" rel="noopener noreferrer"
                        sx={{ bgcolor: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.8)', gap: 0.75,
                              borderRadius: 2, px: 1.5, py: 0.75, display: 'flex', alignItems: 'center',
                              '&:hover': { bgcolor: 'rgba(255,255,255,0.13)', color: 'white' } }}
                      >
                        <LanguageIcon sx={{ fontSize: 18 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {band.performanceLink.replace(/^https?:\/\/(www\.)?/, '')}
                        </Typography>
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Divider if both exist */}
                  {band.performanceLink && viewConfig.showSocialMedia && (band.instagramHandle || band.twitterHandle || band.facebookPage || band.tikTokHandle) && (
                    <Box sx={{ width: '1px', height: 28, bgcolor: 'rgba(255,255,255,0.12)', mx: 0.5 }} />
                  )}

                  {/* Social icons */}
                  {viewConfig.showSocialMedia && (
                    <>
                      {band.instagramHandle && (
                        <Tooltip title={`@${band.instagramHandle}`}>
                          <IconButton component="a" href={`https://instagram.com/${band.instagramHandle}`}
                            target="_blank" rel="noopener noreferrer"
                            sx={{ bgcolor: 'rgba(225,48,108,0.12)', color: '#e1306c', '&:hover': { bgcolor: 'rgba(225,48,108,0.22)' } }}>
                            <InstagramIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {band.twitterHandle && (
                        <Tooltip title={`@${band.twitterHandle}`}>
                          <IconButton component="a" href={`https://twitter.com/${band.twitterHandle}`}
                            target="_blank" rel="noopener noreferrer"
                            sx={{ bgcolor: 'rgba(29,161,242,0.12)', color: '#1da1f2', '&:hover': { bgcolor: 'rgba(29,161,242,0.22)' } }}>
                            <TwitterIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {band.facebookPage && (
                        <Tooltip title="Facebook">
                          <IconButton component="a" href={band.facebookPage}
                            target="_blank" rel="noopener noreferrer"
                            sx={{ bgcolor: 'rgba(24,119,242,0.12)', color: '#1877f2', '&:hover': { bgcolor: 'rgba(24,119,242,0.22)' } }}>
                            <FacebookIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {band.tikTokHandle && (
                        <Tooltip title={`@${band.tikTokHandle}`}>
                          <IconButton component="a" href={`https://www.tiktok.com/@${band.tikTokHandle}`}
                            target="_blank" rel="noopener noreferrer"
                            sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
                            <TikTokIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* YouTube Embeds */}
          {band.youtubeEmbeds && band.youtubeEmbeds.length > 0 && (
            <Box mb={3}>
              <Typography variant="h5" gutterBottom>Videos</Typography>
              {band.youtubeEmbeds.map((embed, index) => (
                <Box
                  key={index}
                  mb={2}
                  sx={{ '& iframe': { width: '100%', height: 'clamp(200px, 56.25vw, 600px)', borderRadius: 1 } }}
                  dangerouslySetInnerHTML={{ __html: embed }}
                />
              ))}
            </Box>
          )}

          {/* Spotify Embeds */}
          {band.spotifyEmbeds && band.spotifyEmbeds.length > 0 && (
            <Box mb={3}>
              <Typography variant="h5" gutterBottom>Spotify</Typography>
              {band.spotifyEmbeds.map((embed, index) => (
                <Box key={index} mb={2} dangerouslySetInnerHTML={{ __html: embed }} />
              ))}
            </Box>
          )}

          {/* SoundCloud Embeds */}
          {band.soundcloudEmbeds && band.soundcloudEmbeds.length > 0 && (
            <Box mb={3}>
              <Typography variant="h5" gutterBottom>SoundCloud</Typography>
              {band.soundcloudEmbeds.map((embed, index) => (
                <Box key={index} mb={2} dangerouslySetInnerHTML={{ __html: embed }} />
              ))}
            </Box>
          )}

          {/* Bandcamp — handles both iframe embed codes and plain URLs */}
          {band.bandcampEmbeds && band.bandcampEmbeds.length > 0 && (
            <Box mb={3}>
              <Typography variant="h5" gutterBottom>Bandcamp</Typography>
              {band.bandcampEmbeds.map((embed, index) =>
                embed.trim().toLowerCase().startsWith('<iframe') ? (
                  <Box
                    key={index}
                    mb={2}
                    sx={{ '& iframe': { width: '100%', borderRadius: 1 } }}
                    dangerouslySetInnerHTML={{ __html: embed }}
                  />
                ) : (
                  <Box key={index} mb={1}>
                    <MuiLink href={embed} target="_blank" rel="noopener noreferrer" sx={{ color: '#4fc3f7', wordBreak: 'break-all' }}>
                      {embed}
                    </MuiLink>
                  </Box>
                )
              )}
            </Box>
          )}

          {/* Other Music Links */}
          {band.musicLinks && band.musicLinks.length > 0 && (
            <Box mb={3}>
              <Typography variant="h5" gutterBottom>Music Links</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {band.musicLinks.map((link, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <MuiLink
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: '#4fc3f7', wordBreak: 'break-all', flex: 1 }}
                    >
                      {link.label || link.url}
                    </MuiLink>
                    {link.url.toLowerCase().endsWith('.mp3') && (
                      <Chip label="MP3" size="small" color="warning" variant="outlined" />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          </Box>}
        </Box>
        );
      })()}

      {/* PRESS & REVIEWS TAB */}
{band.externalValidation && band.externalValidation.length > 0 && (
  <TabPanel value={activeTab} index={tabs.findIndex(t => t.label === 'Press & Reviews')}>
    {viewConfig.showAdminActions && (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Tooltip title="Edit Press & Reviews">
          <IconButton size="small" component={Link} to={`/bandrecommendations/${band._id}/edit?step=Press%20%26%20Awards`} sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    )}
    <PressReviewsTab validations={band.externalValidation} />
  </TabPanel>
)}

      {/* TECHNICAL & FINANCIAL TAB */}
      {(viewConfig.showTechnicalRequirements || viewConfig.showFinancialDetails) && (
        <TabPanel value={activeTab} index={tabs.findIndex(t => t.label === 'Technical & Financial')}>
          <Grid container spacing={3}>
            {/* Technical Requirements */}
            {viewConfig.showTechnicalRequirements && band.technicalRider && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h5">Technical Requirements</Typography>
                      {viewConfig.showAdminActions && (
                        <Tooltip title="Edit Technical Requirements">
                          <IconButton size="small" component={Link} to={`/bandrecommendations/${band._id}/edit?step=Technical%20Requirements`} sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    {band.technicalRider.stageSize && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Stage Size:</strong> {band.technicalRider.stageSize}
                      </Typography>
                    )}
                    {band.technicalRider.audioRequirements && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Audio:</strong> {band.technicalRider.audioRequirements}
                      </Typography>
                    )}
                    {band.technicalRider.lightingRequirements && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Lighting:</strong> {band.technicalRider.lightingRequirements}
                      </Typography>
                    )}
                    {band.technicalRider.additionalEquipment && band.technicalRider.additionalEquipment.length > 0 && (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>Additional Equipment:</Typography>
                        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                          {band.technicalRider.additionalEquipment.map((item, index) => (
                            <li key={index}><Typography variant="body2">{item}</Typography></li>
                          ))}
                        </ul>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Financial Information */}
            {viewConfig.showFinancialDetails && band.financialDetails && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h5">Financial Information</Typography>
                      {viewConfig.showAdminActions && (
                        <Tooltip title="Edit Financial Information">
                          <IconButton size="small" component={Link} to={`/bandrecommendations/${band._id}/edit?step=Financial%20Information`} sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    {band.financialDetails.minimumFee ? (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Minimum Fee:</strong> {band.financialDetails.minimumFee} {band.financialDetails.preferredCurrency || ''}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Minimum Fee:</strong> Not specified
                      </Typography>
                    )}
                    {band.financialDetails.accommodationNeeded !== undefined && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Accommodation:</strong> {band.financialDetails.accommodationNeeded ? 'Required' : 'Not required'}
                      </Typography>
                    )}
                    {band.financialDetails.transportationNeeded !== undefined && (
                      <Typography variant="body2">
                        <strong>Transportation:</strong> {band.financialDetails.transportationNeeded ? 'Required' : 'Not required'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>
      )}

      {/* ADMIN TAB */}
      {viewConfig.showAdminActions && (
        <TabPanel value={activeTab} index={tabs.findIndex(t => t.label === 'Admin')}>
          <Grid container spacing={3}>
            {/* Admin Actions */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom>Actions</Typography>
                  <Button 
                    variant="contained" 
                    color="success" 
                    onClick={handleAddToEvent} 
                    fullWidth 
                    sx={{ mb: 1 }}
                  >
                    Add to Event
                  </Button>
                  <Button 
                    variant="contained" 
                    color={(band.publishedTo && band.publishedTo.length > 0) ? "info" : "primary"}
                    onClick={handlePublish} 
                    fullWidth 
                    sx={{ mb: 1 }}
                  >
                    {(band.publishedTo && band.publishedTo.length > 0)
                      ? `Manage Publications (${band.publishedTo.length})` 
                      : "Publish"}
                  </Button>
                  <Button
                    variant="contained"
                    color={band.isDeactivated ? 'success' : 'warning'}
                    onClick={handleToggleDeactivate}
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    {band.isDeactivated ? 'Reactivate Band Page' : 'Deactivate Band Page'}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePending}
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    Pending
                  </Button>
                  <Button 
                    variant="contained" 
                    color="secondary" 
                    onClick={handleEdit} 
                    fullWidth 
                    sx={{ mb: 1 }}
                  >
                    <EditIcon sx={{ mr: 1 }} />
                    Edit
                  </Button>
                  <Button 
                    variant="contained" 
                    color="error" 
                    onClick={handleDelete} 
                    fullWidth
                  >
                    Delete
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Published Strands */}
            <Grid item xs={12} md={4}>
              <PublishedStrandsCard 
                bandId={band._id || ''} 
                publishedTo={band.publishedTo}
              />
            </Grid>

            {/* Availability & Booking Details */}
            <Grid item xs={12} md={4}>
              {viewConfig.showAvailability && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h5">Availability</Typography>
                      {viewConfig.showAdminActions && (
                        <Tooltip title="Edit Availability">
                          <IconButton size="small" component={Link} to={`/bandrecommendations/${band._id}/edit?step=Availability`} sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 0 } }}>
                      <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography variant="h4" sx={{ fontSize: { xs: '1.4rem', md: '2.125rem' } }}>{availabilityFrom.month}</Typography>
                        <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{availabilityFrom.day}</Typography>
                        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>{availabilityFrom.year}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', px: { sm: 1 } }}>
                        <ArrowForwardIcon fontSize="large" sx={{ transform: { xs: 'rotate(90deg)', sm: 'none' } }} />
                      </Box>
                      <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography variant="h4" sx={{ fontSize: { xs: '1.4rem', md: '2.125rem' } }}>{availabilityTo.month}</Typography>
                        <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{availabilityTo.day}</Typography>
                        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>{availabilityTo.year}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {viewConfig.showRecommenderInfo && (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h5">Booking Details</Typography>
                      {viewConfig.showAdminActions && (
                        <Tooltip title="Edit Booking Contact">
                          <IconButton size="small" component={Link} to={`/bandrecommendations/${band._id}/edit?step=Contact%20Information`} sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Typography variant="body2"><strong>Name:</strong> {band.recommenderName}</Typography>
                    <Typography variant="body2"><strong>Email:</strong> {band.recommenderEmail}</Typography>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </TabPanel>
      )}

      {/* ── Meet the Musicians ── */}
      {(() => {
        // Collapse instrumentation into unique performers
        const performerMap: Record<string, string[]> = {};
        (band.instrumentation || []).forEach(i => {
          if (!i.performer?.name) return;
          if (!performerMap[i.performer.name]) performerMap[i.performer.name] = [];
          if (i.instrumentName) performerMap[i.performer.name].push(i.instrumentName);
        });
        const performers = Object.entries(performerMap);
        if (!performers.length) return null;
        return (
          <Box sx={{ mt: 5, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>Meet the Musicians</Typography>
              <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
            </Box>
            <Box sx={{
              display: 'flex', gap: 2, flexWrap: { xs: 'nowrap', md: 'wrap' },
              overflowX: { xs: 'auto', md: 'visible' }, pb: { xs: 1.5, md: 0 },
              scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.15) transparent',
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 },
            }}>
              {performers.map(([name, instruments]) => (
                <Card
                  key={name}
                  component={Link}
                  to={`/musician/${encodeURIComponent(name)}`}
                  sx={{
                    minWidth: { xs: 145, md: 160 },
                    maxWidth: { xs: 145, md: 160 },
                    flexShrink: 0,
                    textDecoration: 'none',
                    bgcolor: 'background.paper',
                    border: '1px solid rgba(255,255,255,0.06)',
                    transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                    {/* Avatar circle with initials */}
                    <Box sx={{
                      width: 44, height: 44, borderRadius: '50%',
                      bgcolor: 'primary.main', opacity: 0.15,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mb: 1.25, position: 'relative',
                    }}>
                      <Box sx={{
                        position: 'absolute', inset: 0, borderRadius: '50%',
                        bgcolor: 'primary.main', opacity: 0.15,
                      }} />
                      <Typography sx={{
                        fontSize: '1rem', fontWeight: 700, color: 'primary.main',
                        lineHeight: 1, position: 'relative',
                      }}>
                        {name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                      </Typography>
                    </Box>
                    <Typography variant="subtitle2" fontWeight={700}
                      sx={{ color: 'text.primary', lineHeight: 1.25, mb: 0.75, fontSize: '0.82rem' }}>
                      {name}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                      {instruments.slice(0, 2).map(inst => (
                        <Chip key={inst} label={performerTitleByInstrument[inst] ?? inst} size="small"
                          sx={{ fontSize: '0.6rem', height: 16, bgcolor: 'rgba(79,195,247,0.1)', color: '#4fc3f7' }} />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        );
      })()}

      {/* ── Segmented Recommendations ── */}
      {similarSegments.length > 0 && (
        <Box sx={{ mt: 5, mb: 2 }}>
          {similarSegments.map((segment) => (
            <Box key={segment.label} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>
                  {segment.label}
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  overflowX: 'auto',
                  pb: 1.5,
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(255,255,255,0.15) transparent',
                  '&::-webkit-scrollbar': { height: 4 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 },
                }}
              >
                {segment.bands.map((sb) => (
                  <Card
                    key={sb._id}
                    component={Link}
                    to={`/band/${sb._id}`}
                    sx={{
                      minWidth: { xs: 155, md: 195 },
                      maxWidth: { xs: 155, md: 195 },
                      flexShrink: 0,
                      textDecoration: 'none',
                      display: 'block',
                      bgcolor: 'background.paper',
                      transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height={125}
                      image={sb.mainImage || '/placeholder-image.jpg'}
                      alt={sb.artistName}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ mb: 0.5, color: 'text.primary' }}>
                        {sb.artistName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.6 }}>
                        {sb.maleMembers + sb.femaleMembers} {sb.maleMembers + sb.femaleMembers === 1 ? 'member' : 'members'}
                      </Typography>
                      {/* Performer segment: "feat. bassist Derek Whyte" */}
                      {segment.type === 'performer' && sb._sharedPerformers && sb._sharedPerformers.length > 0 && (
                        <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 0.5, fontStyle: 'italic' }}>
                          feat.{' '}
                          {sb._sharedPerformers.slice(0, 2).map((p, i) => {
                            const title = performerTitleByInstrument[p.instrument] ?? '';
                            return (i > 0 ? ', ' : '') + (title ? `${title} ${p.name}` : p.name);
                          })}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                        {sb.genres.slice(0, 2).map(g => (
                          <Chip key={g} label={g} size="small"
                            sx={{ fontSize: '0.62rem', height: 17, bgcolor: 'rgba(79,195,247,0.1)', color: '#4fc3f7' }} />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>

      {/* Dialogs */}
      <Dialog open={openAddToEventDialog} onClose={() => setOpenAddToEventDialog(false)}>
        <DialogTitle>Add {band.artistName} to Event</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Event</InputLabel>
            <Select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value as string)}
            >
              {events.map((event) => (
                <MenuItem key={event._id} value={event._id}>
                  {event.name} - {new Date(event.date).toLocaleDateString()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleCreateNewEvent}
            fullWidth
            sx={{ mt: 2 }}
          >
            Create New Event
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddToEventDialog(false)}>Cancel</Button>
          <Button onClick={handleAddToExistingEvent} variant="contained" color="primary">
            Add to Event
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <strong>{band.artistName}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <PublishBandModal
        open={openPublishModal}
        onClose={handleModalClose}  // Use the new handler
        bandId={band?._id || ''}
        bandName={band?.artistName || ''}
        onPublish={handlePublishBand}
      />

            {/* Band QR Modal */}
      <BandQRModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        bandId={band?._id || ''}
        bandName={band?.artistName || ''}
      />
    </Container>
  </>
  );
};

export default BandDetailPage;